import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  BarChart3, Megaphone, Rocket, Share2, Gift, ArrowLeft, Loader2, Users, CreditCard,
  TrendingUp, Mail, MessageCircle, Sparkles, Flame, Copy, RefreshCw, ImageIcon, Download, ExternalLink,
  Inbox, CheckCircle2, Reply, Mic, Sprout, Trash2, Video, Music, Clapperboard, Radio, Activity, Eye,
  Globe, Smartphone, Send, XCircle, Clock,
} from "lucide-react";
import { NEWSLETTER_TEMPLATES } from "@/data/newsletterTemplates";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  AreaChart, Area, Legend,
} from "recharts";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ConferencesSection from "@/components/admin/ConferencesSection";
import PartnersLetterSection from "@/components/admin/PartnersLetterSection";
import UsersSection from "@/components/admin/UsersSection";
import AnalyticsSection from "@/components/admin/AnalyticsSection";
import MaintenanceSection from "@/components/admin/MaintenanceSection";
import { Handshake, Users2, Wrench, UserPlus, Radio as RadioIcon } from "lucide-react";
import { RadioChannelsAdmin } from "@/components/RadioChannelsAdmin";

const API = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { id: "kpis", label: "Croissance", icon: BarChart3 },
  { id: "monitoring", label: "Monitoring", icon: Activity },
  { id: "promo", label: "Contenu promo", icon: Megaphone },
  { id: "tiktok", label: "Scripts TikTok", icon: Video },
  { id: "derive", label: "La Dérive", icon: Radio },
  { id: "newsletter", label: "Newsletter", icon: Mail },
  { id: "playbook", label: "Playbook", icon: Rocket },
  { id: "kit", label: "Kit de partage", icon: Share2 },
  { id: "referral", label: "Parrainage", icon: Gift },
  { id: "conferences", label: "Conférences", icon: Mic },
  { id: "analytics", label: "Statistiques", icon: BarChart3 },
  { id: "partners", label: "Partenariats", icon: Handshake },
  { id: "users", label: "Utilisateurs", icon: Users2 },
  { id: "garden", label: "Jardin des idées", icon: Sprout },
  { id: "messages", label: "Messages", icon: Inbox },
  { id: "guests", label: "Sujets proposés", icon: UserPlus },
  { id: "radio", label: "Chaînes radio", icon: RadioIcon },
  { id: "maintenance", label: "Maintenance", icon: Wrench },
];

const PLAYBOOK = [
  { channel: "Product Hunt", tip: "Lance Cocoon un mardi ou mercredi. Prépare 5 visuels cinématiques, une vidéo de 30 s du Fil, et mobilise tes premiers utilisateurs pour commenter dès 00 h 01 (PST).", template: "🚀 COCOON — le précepteur IA anti-école. Zéro note, zéro programme : juste ta curiosité et un mentor qui l'explore avec toi. Le Fil, la Constellation, le Carnet d'épiphanies…" },
  { channel: "Reddit", tip: "Vise r/education, r/GetStudying, r/artificial, r/homeschool. Ne vends pas : raconte une histoire (« J'ai construit un tuteur sans notes ni programme »). Apporte de la valeur d'abord.", template: "J'ai créé un mentor IA qui n'évalue jamais et suit ta curiosité au lieu d'un programme. Retour d'expérience et démo en commentaire." },
  { channel: "TikTok / Reels", tip: "Format « Saviez-vous que ? » et « Pourquoi dit-on… ? » : reprends tes cartes de la home en courtes vidéos verticales avec voix off. 3 à 5 par semaine, hooks forts.", template: "POV : tu poses une question à ton tuteur et il te répond comme lors d'une promenade en forêt 🌲✨ #apprendreautrement #curiosité" },
  { channel: "Communautés parents & écoles alternatives", tip: "Groupes Facebook Montessori, IEF (instruction en famille), écoles démocratiques. Ton message résonne fort avec le « zéro pression ».", template: "Pour les curieux de tout âge : un compagnon d'apprentissage sans notes ni compétition, pensé pour le plaisir de comprendre." },
  { channel: "SEO / Blog", tip: "Publie des articles « Pourquoi dit-on… », « Comment expliquer X à un enfant ». Chaque sujet de L'Écho peut devenir un article indexable qui ramène du trafic organique.", template: "Cible des requêtes longue traîne (« pourquoi le ciel est bleu explication simple ») avec un lien vers Le Fil." },
  { channel: "LinkedIn (fondateur)", tip: "Poste ton « pourquoi » : ta vision de l'apprentissage. Les posts personnels du fondateur convertissent mieux que la page produit. 2 posts / semaine.", template: "L'école m'a appris à réussir des examens. Cocoon veut réapprendre à s'émerveiller. Voici pourquoi je l'ai construit 🧵" },
  { channel: "Parrainage & bouche-à-oreille", tip: "Active l'onglet Parrainage : chaque explorateur satisfait est ton meilleur canal. Mets en avant le feu de camp partagé (Le Cercle) — on invite naturellement un ami.", template: "Partage ton lien de parrainage et explore un sujet à plusieurs autour d'un feu de camp avec L'Élan." },
];

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="glass rounded-2xl p-5 frame-pulse" data-testid={`stat-${label}`}>
      <div className="flex items-center gap-2 text-foreground/50 text-xs uppercase tracking-wider mb-3">
        <Icon size={15} className="text-primary" /> {label}
      </div>
      <div className={`font-display text-3xl ${accent ? "text-gradient-orange" : "text-foreground/90"}`}>{value}</div>
      {sub && <div className="text-xs text-foreground/40 mt-1">{sub}</div>}
    </div>
  );
}

function GrantAccessCard({ onGranted }) {
  const [email, setEmail] = useState("");
  const [plan, setPlan] = useState("cocon");
  const [loading, setLoading] = useState(false);
  const grant = async () => {
    const e = email.trim();
    if (!e || loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/admin/grant-access", { email: e, plan });
      toast.success(`✓ ${data.name || data.email} — ${plan === "cocon" ? "Le Cocoon" : "L'Élan"} débloqué${data.welcome_sent ? " · e-mail de bienvenue envoyé" : " · ⚠️ e-mail NON envoyé"}`);
      setEmail("");
      onGranted?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="glass rounded-2xl p-6 frame-pulse" data-testid="grant-access-card">
      <div className="flex items-center gap-2 text-foreground/70 text-sm mb-1">
        <CheckCircle2 size={16} className="text-primary" /> Donner l'accès (paiement manuel)
      </div>
      <p className="text-xs text-foreground/40 mb-4">Débloque un abonnement pour un compte existant (ex. un client qui a payé). Envoie aussi l'e-mail de bienvenue.</p>
      <div className="flex flex-col sm:flex-row items-stretch gap-2">
        <input
          data-testid="grant-email-input"
          value={email}
          onChange={(ev) => setEmail(ev.target.value)}
          onKeyDown={(ev) => ev.key === "Enter" && grant()}
          placeholder="email@du-client.be"
          className="flex-1 bg-black/30 border border-white/15 rounded-full px-4 py-2.5 text-sm outline-none text-foreground placeholder:text-foreground/30 focus:border-primary/50 transition-colors"
        />
        <select
          data-testid="grant-plan-select"
          value={plan}
          onChange={(ev) => setPlan(ev.target.value)}
          className="bg-black/30 border border-white/15 rounded-full px-4 py-2.5 text-sm outline-none text-foreground focus:border-primary/50"
        >
          <option value="cocon">Le Cocoon</option>
          <option value="elan">L'Élan</option>
        </select>
        <button
          data-testid="grant-submit-btn"
          onClick={grant}
          disabled={loading || !email.trim()}
          className="btn-frost-orange inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full font-semibold text-sm disabled:opacity-40"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Débloquer
        </button>
      </div>
    </div>
  );
}

function GrantsRecap({ refreshKey }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/admin/grants").then(({ data }) => setRows(data.grants || [])).catch(() => setRows([]));
  }, [refreshKey]);
  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso.slice(0, 16).replace("T", " "); }
  };
  return (
    <div className="glass rounded-2xl p-6" data-testid="grants-recap-card">
      <div className="flex items-center gap-2 text-foreground/70 text-sm mb-1">
        <Gift size={16} className="text-primary" /> Accès donnés manuellement
      </div>
      <p className="text-xs text-foreground/40 mb-4">Historique des personnes à qui vous avez débloqué un abonnement, et si l'e-mail de bienvenue est bien parti.</p>
      {rows === null ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-primary/60" size={22} /></div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-foreground/40 py-4 text-center">Aucun accès donné pour l'instant.</p>
      ) : (
        <div className="space-y-1.5 max-h-96 overflow-y-auto scroll-thin" data-testid="grants-recap-list">
          {rows.map((g, i) => (
            <div key={i} className="flex items-center gap-3 py-2 px-3 rounded-xl bg-white/[0.02] border border-white/5" data-testid="grant-row">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-foreground/90 truncate">{g.name || "—"} <span className="text-foreground/40">· {g.email}</span></div>
                <div className="text-[11px] text-foreground/35 flex items-center gap-1.5 mt-0.5">
                  <Clock size={10} /> {fmt(g.granted_at)}
                </div>
              </div>
              <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-primary/12 text-primary/90 flex-shrink-0">
                {g.plan === "cocon" ? "Le Cocoon" : g.plan === "elan" ? "L'Élan" : g.plan}
              </span>
              {g.welcome_sent ? (
                <span className="text-[11px] text-emerald-400/90 flex items-center gap-1 flex-shrink-0" title="E-mail de bienvenue envoyé">
                  <Mail size={13} /> envoyé
                </span>
              ) : (
                <span className="text-[11px] text-red-400/80 flex items-center gap-1 flex-shrink-0" title="E-mail de bienvenue NON envoyé">
                  <XCircle size={13} /> non envoyé
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KpisSection() {
  const [stats, setStats] = useState(null);
  const [range, setRange] = useState(7);
  const [grantRefresh, setGrantRefresh] = useState(0);
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);
  if (!stats) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary/60" size={28} /></div>;
  const chartData = (range === 7 ? stats.series7 : stats.series30).map((d) => ({ ...d, day: d.date.slice(5) }));
  return (
    <div className="space-y-8" data-testid="kpis-section">
      <GrantAccessCard onGranted={() => setGrantRefresh((x) => x + 1)} />
      <GrantsRecap refreshKey={grantRefresh} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Explorateurs" value={stats.explorers} sub={`+${stats.new_week} cette semaine`} accent />
        <StatCard icon={CreditCard} label="Abonnés payants" value={stats.paid} sub={`Élan ${stats.elan} · Cocoon ${stats.cocon}`} />
        <StatCard icon={TrendingUp} label="Taux de conversion" value={`${stats.conversion}%`} sub="payants / inscrits" />
        <StatCard icon={Sparkles} label="MRR estimé" value={`${stats.mrr} €`} sub={`ARR ≈ ${stats.arr} €`} accent />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Mail} label="Newsletter" value={stats.newsletter} sub="abonnés" />
        <StatCard icon={MessageCircle} label="Conversations" value={stats.conversations} />
        <StatCard icon={Flame} label="Feu de camp" value={stats.campfire} sub={`${stats.duos} Cercles créés`} />
        <StatCard icon={Gift} label="Parrainages" value={stats.referred} sub="inscrits via un lien" />
      </div>
      <div className="glass rounded-2xl p-6 frame-pulse">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl text-foreground/85">Nouvelles inscriptions</h3>
          <div className="flex gap-1">
            {[7, 30].map((n) => (
              <button key={n} data-testid={`range-${n}`} onClick={() => setRange(n)}
                className={`px-3 py-1.5 rounded-full text-xs transition-colors ${range === n ? "bg-primary/20 text-primary" : "text-foreground/40 hover:text-foreground/70"}`}>
                {n} jours
              </button>
            ))}
          </div>
        </div>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="day" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} interval={range === 30 ? 4 : 0} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "rgba(224,138,43,0.08)" }} contentStyle={{ background: "hsl(215 25% 9%)", border: "1px solid rgba(224,138,43,0.3)", borderRadius: 12, color: "#efe6d0" }} />
              <Bar dataKey="count" fill="#e08a2b" radius={[4, 4, 0, 0]} maxBarSize={38} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function BreakdownCard({ icon: Icon, title, items, testId }) {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div className="glass rounded-2xl p-6" data-testid={testId}>
      <div className="flex items-center gap-2 text-foreground/60 text-sm mb-4">
        <Icon size={16} className="text-primary" /> {title}
      </div>
      {items.length === 0 ? (
        <p className="text-foreground/30 text-sm italic">Aucune donnée</p>
      ) : (
        <ul className="space-y-3">
          {items.map((it, i) => (
            <li key={i} data-testid={`${testId}-row`}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-foreground/75 truncate max-w-[70%]">{it.label}</span>
                <span className="text-foreground/45 tabular-nums">{it.count}</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full rounded-full bg-primary/70" style={{ width: `${(it.count / max) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MonitoringSection() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState(30);
  const [loading, setLoading] = useState(true);

  const fetchData = (days) => {
    setLoading(true);
    api.get(`/admin/monitoring?days=${days}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetchData(range); }, [range]);

  if (loading || !data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary/60" size={28} /></div>;

  const chartData = data.series.map((d) => ({ ...d, day: d.date.slice(5) }));

  return (
    <div className="space-y-8" data-testid="monitoring-section">
      <p className="text-xs text-foreground/40 -mt-2">Analytics respectueux de la vie privée · anonyme, sans cookies.</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Eye} label="Visites aujourd'hui" value={data.totals.today} sub={`${data.uniques.today} visiteurs uniques`} accent />
        <StatCard icon={Activity} label="Visites 7 jours" value={data.totals.d7} sub={`${data.uniques.d7} uniques`} />
        <StatCard icon={TrendingUp} label="Visites 30 jours" value={data.totals.d30} sub={`${data.uniques.d30} uniques`} />
        <StatCard icon={Users} label="Visites totales" value={data.totals.all} sub="depuis le début" accent />
      </div>

      <div className="glass rounded-2xl p-6 frame-pulse">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl text-foreground/85">Fréquentation</h3>
          <div className="flex gap-1">
            {[7, 30].map((n) => (
              <button key={n} data-testid={`monitoring-range-${n}`} onClick={() => setRange(n)}
                className={`px-3 py-1.5 rounded-full text-xs transition-colors ${range === n ? "bg-primary/20 text-primary" : "text-foreground/40 hover:text-foreground/70"}`}>
                {n} jours
              </button>
            ))}
          </div>
        </div>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gVisits" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#e08a2b" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#e08a2b" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gUniques" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#7dd3fc" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#7dd3fc" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="day" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} interval={range === 30 ? 4 : 0} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} width={40} />
              <Tooltip contentStyle={{ background: "hsl(215 25% 9%)", border: "1px solid rgba(224,138,43,0.3)", borderRadius: 12, color: "#efe6d0" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="visits" name="Visites" stroke="#e08a2b" strokeWidth={2} fill="url(#gVisits)" />
              <Area type="monotone" dataKey="uniques" name="Uniques" stroke="#7dd3fc" strokeWidth={2} fill="url(#gUniques)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <BreakdownCard icon={Eye} title="Pages les plus vues (30 j)" items={data.top_pages} testId="mon-pages" />
        <BreakdownCard icon={Share2} title="Provenance (30 j)" items={data.sources} testId="mon-sources" />
        <BreakdownCard icon={Smartphone} title="Appareils (30 j)" items={data.devices} testId="mon-devices" />
        <BreakdownCard icon={Globe} title="Langues (30 j)" items={data.languages} testId="mon-langs" />
      </div>
    </div>
  );
}


function PromoSection() {
  const [platform, setPlatform] = useState("twitter");
  const [lang, setLang] = useState("fr");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/promo/generate", { platform, lang });
      setText(data.text);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur de génération");
    } finally { setLoading(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success("Post copié"); }
    catch { toast.error("Copie impossible, sélectionne le texte manuellement."); }
  };
  return (
    <div className="glass rounded-2xl p-6 frame-pulse space-y-5" data-testid="promo-section">
      <p className="text-foreground/60">L'Élan rédige un post prêt à publier dans le ton de Cocoon. Choisis le réseau et la langue.</p>
      <div className="flex flex-wrap gap-2">
        {[["twitter", "X / Twitter"], ["linkedin", "LinkedIn"], ["instagram", "Instagram"]].map(([id, lbl]) => (
          <button key={id} data-testid={`promo-platform-${id}`} onClick={() => setPlatform(id)}
            className={`px-4 py-2 rounded-full text-sm border transition-colors ${platform === id ? "border-primary bg-primary/15 text-primary" : "border-white/15 text-foreground/50 hover:text-foreground/80"}`}>
            {lbl}
          </button>
        ))}
        <select data-testid="promo-lang" value={lang} onChange={(e) => setLang(e.target.value)}
          className="ml-auto bg-black/30 border border-white/15 rounded-full px-4 py-2 text-sm text-foreground/70 outline-none">
          <option value="fr">Français</option><option value="en">English</option>
          <option value="es">Español</option><option value="nl">Nederlands</option>
        </select>
      </div>
      <button data-testid="promo-generate-btn" onClick={generate} disabled={loading}
        className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm disabled:opacity-50">
        {loading ? <Loader2 size={17} className="animate-spin" /> : <Megaphone size={17} />} Générer un post
      </button>
      {text && (
        <div className="relative">
          <textarea data-testid="promo-output" readOnly value={text} rows={7}
            className="w-full bg-black/30 border border-white/10 rounded-2xl p-4 text-foreground/85 leading-relaxed resize-none outline-none whitespace-pre-line" />
          <button data-testid="promo-copy-btn" onClick={copy} className="absolute top-3 right-3 text-primary hover:text-primary/80" title="Copier">
            <Copy size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

function TikTokSection() {
  const { t } = useTranslation();
  const debates = (() => { try { return t("arene.debates", { returnObjects: true }) || []; } catch { return []; } })();
  const [lang, setLang] = useState("fr");
  const [source, setSource] = useState("libre"); // libre | arene | custom
  const [debateIdx, setDebateIdx] = useState(0);
  const [custom, setCustom] = useState("");
  const [script, setScript] = useState(null);
  const [loading, setLoading] = useState(false);

  const seedFor = () => {
    if (source === "arene") return debates[debateIdx]?.question || "";
    if (source === "custom") return custom.trim();
    return "";
  };

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/promo/tiktok", { lang, seed: seedFor() });
      setScript(data.script);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur de génération");
    } finally { setLoading(false); }
  };

  const scriptToText = (s) => {
    if (!s) return "";
    const beats = (s.beats || []).map((b) => `• [${b.time || ""}] ${b.onscreen || ""}${b.voiceover ? `\n   voix : ${b.voiceover}` : ""}`).join("\n");
    return [
      `HOOK : ${s.hook || ""}`, "",
      "DÉROULÉ :", beats, "",
      `IDÉE VISUELLE : ${s.visual_idea || ""}`,
      `SON : ${s.sound_tip || ""}`, "",
      `LÉGENDE : ${s.caption || ""}`,
      `CTA : ${s.cta || ""}`,
      `HASHTAGS : ${(s.hashtags || []).join(" ")}`,
    ].join("\n");
  };

  const copyAll = async () => {
    try { await navigator.clipboard.writeText(scriptToText(script)); toast.success("Script copié"); }
    catch { toast.error("Copie impossible."); }
  };

  return (
    <div className="glass rounded-2xl p-6 frame-pulse space-y-5" data-testid="tiktok-section">
      <p className="text-foreground/60">L'Élan écrit un script de vidéo verticale (TikTok / Reels) prêt à tourner : hook, déroulé seconde par seconde, idée visuelle, son, légende et hashtags.</p>

      <div className="flex flex-wrap gap-2">
        {[["libre", "Angle libre"], ["arene", "Un débat de L'Arène"], ["custom", "Un sujet précis"]].map(([id, lbl]) => (
          <button key={id} data-testid={`tiktok-source-${id}`} onClick={() => setSource(id)}
            className={`px-4 py-2 rounded-full text-sm border transition-colors ${source === id ? "border-primary bg-primary/15 text-primary" : "border-white/15 text-foreground/50 hover:text-foreground/80"}`}>
            {lbl}
          </button>
        ))}
        <select data-testid="tiktok-lang" value={lang} onChange={(e) => setLang(e.target.value)}
          className="ml-auto bg-black/30 border border-white/15 rounded-full px-4 py-2 text-sm text-foreground/70 outline-none">
          <option value="fr">Français</option><option value="en">English</option>
          <option value="es">Español</option><option value="nl">Nederlands</option>
        </select>
      </div>

      {source === "arene" && debates.length > 0 && (
        <select data-testid="tiktok-debate" value={debateIdx} onChange={(e) => setDebateIdx(Number(e.target.value))}
          className="w-full bg-black/30 border border-white/15 rounded-xl px-4 py-3 text-sm text-foreground/80 outline-none">
          {debates.map((d, i) => <option key={d.id || i} value={i}>{d.question}</option>)}
        </select>
      )}
      {source === "custom" && (
        <input data-testid="tiktok-custom" value={custom} onChange={(e) => setCustom(e.target.value)}
          placeholder="Ex. : Pourquoi le ciel est-il bleu ? / Le mérite à l'ère de l'IA…"
          className="w-full bg-black/30 border border-white/15 rounded-xl px-4 py-3 text-sm text-foreground/80 outline-none placeholder:text-foreground/30" />
      )}

      <button data-testid="tiktok-generate-btn" onClick={generate} disabled={loading || (source === "custom" && !custom.trim())}
        className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm disabled:opacity-50">
        {loading ? <Loader2 size={17} className="animate-spin" /> : <Clapperboard size={17} />} Générer un script
      </button>

      {script && (
        <div className="space-y-4" data-testid="tiktok-output">
          <div className="relative rounded-2xl border border-primary/25 bg-black/25 p-5">
            <button data-testid="tiktok-copy-btn" onClick={copyAll} className="absolute top-4 right-4 text-primary hover:text-primary/80" title="Copier le script"><Copy size={16} /></button>

            <div className="flex items-center gap-2 text-primary/70 text-xs uppercase tracking-wider mb-1"><Video size={13} /> Hook</div>
            <p className="font-display text-xl text-gradient-orange mb-4" data-testid="tiktok-hook">« {script.hook} »</p>

            <div className="space-y-2.5 mb-4">
              {(script.beats || []).map((b, i) => (
                <div key={i} className="flex gap-3">
                  <span className="text-xs font-mono text-primary/60 pt-0.5 w-16 flex-shrink-0">{b.time}</span>
                  <div className="flex-1">
                    <p className="text-sm text-foreground/85">{b.onscreen}</p>
                    {b.voiceover && <p className="text-xs text-foreground/45 italic mt-0.5">voix : {b.voiceover}</p>}
                  </div>
                </div>
              ))}
            </div>

            {script.visual_idea && (
              <div className="flex items-start gap-2 text-sm text-foreground/70 mb-2"><Clapperboard size={15} className="text-primary mt-0.5 flex-shrink-0" /><span>{script.visual_idea}</span></div>
            )}
            {script.sound_tip && (
              <div className="flex items-start gap-2 text-sm text-foreground/70 mb-2"><Music size={15} className="text-primary mt-0.5 flex-shrink-0" /><span>{script.sound_tip}</span></div>
            )}

            {script.caption && <p className="text-sm text-foreground/75 mt-3 pt-3 border-t border-white/10">{script.caption}</p>}
            {script.cta && <p className="text-sm text-primary/90 mt-1">{script.cta}</p>}
            {script.hashtags?.length > 0 && (
              <p className="text-xs text-primary/60 mt-2">{(script.hashtags || []).join(" ")}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DeriveSection() {
  const [lang, setLang] = useState("fr");
  const [ep, setEp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const [audience, setAudience] = useState(null);

  useEffect(() => {
    api.get("/admin/derive/audience").then(({ data }) => setAudience(data)).catch(() => {});
  }, []);

  const generate = async (regenerate) => {
    setLoading(true); setEp(null);
    try {
      const { data } = await api.post("/admin/derive/generate", { lang, regenerate });
      setEp(data);
      toast.success(regenerate ? "Numéro régénéré" : "Numéro prêt");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur de génération");
    } finally { setLoading(false); }
  };

  const broadcast = async () => {
    if (!window.confirm("Diffuser le numéro de la semaine à tous les abonnés (Cocoon + inscrits) par e-mail ? (envoi immédiat)")) return;
    setBroadcasting(true);
    try {
      const { data } = await api.post("/newsletter/send-now");
      toast.success(`Diffusé — ${data.emails_sent} e-mail(s) envoyé(s)`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Diffusion impossible");
    } finally { setBroadcasting(false); }
  };

  const mm = ep ? Math.max(1, Math.round((ep.audio_seconds || 0) / 60)) : 0;

  return (
    <div className="glass rounded-2xl p-6 frame-pulse space-y-5" data-testid="derive-section">
      <p className="text-foreground/60">Génère (ou régénère) le numéro de La Dérive de la semaine en cours, avec sa version audio, pour l'écouter avant diffusion. La génération hebdomadaire automatique reste active (lundi 06:00 UTC).</p>

      {audience && (
        <div className="flex flex-wrap gap-3" data-testid="derive-audience">
          <div className="rounded-xl border border-primary/15 bg-primary/[0.04] px-4 py-2.5">
            <div className="text-lg font-display text-gradient-orange">{audience.cocon}</div>
            <div className="text-[0.7rem] uppercase tracking-wider text-foreground/45">abonnés Cocoon (e-mail)</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-2.5">
            <div className="text-lg font-display text-foreground/90">{audience.landing}</div>
            <div className="text-[0.7rem] uppercase tracking-wider text-foreground/45">inscrits confirmés (page d'accueil)</div>
          </div>
          {audience.pending > 0 && (
            <div className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-2.5" data-testid="derive-pending">
              <div className="text-lg font-display text-foreground/60">{audience.pending}</div>
              <div className="text-[0.7rem] uppercase tracking-wider text-foreground/40">en attente de confirmation</div>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <select data-testid="derive-lang" value={lang} onChange={(e) => setLang(e.target.value)}
          className="bg-black/30 border border-white/15 rounded-full px-4 py-2 text-sm text-foreground/70 outline-none">
          <option value="fr">Français</option><option value="en">English</option>
          <option value="es">Español</option><option value="nl">Nederlands</option>
        </select>
        <button data-testid="derive-generate-btn" onClick={() => generate(false)} disabled={loading}
          className="btn-frost-orange inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm disabled:opacity-50">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Radio size={16} />} Générer le numéro
        </button>
        <button data-testid="derive-regenerate-btn" onClick={() => generate(true)} disabled={loading}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors font-semibold text-sm disabled:opacity-50">
          <RefreshCw size={15} /> Régénérer
        </button>
      </div>

      {loading && <p className="text-sm text-foreground/40 italic">L'Élan compose le numéro et l'enregistre en audio…</p>}

      {ep && (
        <div className="rounded-2xl border border-primary/25 bg-black/25 p-6 space-y-4" data-testid="derive-preview">
          <div className="flex items-center gap-3 text-xs">
            <span className="uppercase tracking-wider text-primary/60">{ep.week_key}</span>
            {ep.theme && <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary/80">{ep.theme}</span>}
            {ep.audio_seconds > 0 && <span className="text-foreground/40 ml-auto">≈ {mm} min</span>}
          </div>
          <h3 className="font-display italic text-2xl text-gradient-orange" data-testid="derive-preview-subject">{ep.subject}</h3>
          {ep.audio_url && <audio controls preload="none" src={ep.audio_url} data-testid="derive-preview-audio" className="w-full" />}
          {ep.fact && (
            <div><div className="text-[0.7rem] uppercase tracking-wider text-primary/70 mb-1">Le saviez-vous ?</div>
              <p className="text-sm text-foreground/75 leading-relaxed">{ep.fact}</p></div>
          )}
          {ep.question && (
            <div><div className="text-[0.7rem] uppercase tracking-wider text-primary/70 mb-1">La question</div>
              <p className="font-display italic text-foreground/85">{ep.question}</p></div>
          )}
          {ep.community_epiphany && (
            <div><div className="text-[0.7rem] uppercase tracking-wider text-primary/70 mb-1">L'épiphanie de la communauté</div>
              <p className="text-sm text-foreground/70 italic">{ep.community_epiphany}</p></div>
          )}
          <div className="pt-2 border-t border-white/10 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-foreground/40">La diffusion génère les 4 langues et envoie l'e-mail aux abonnés Cocoon + inscrits de la page d'accueil.</p>
            <button data-testid="derive-broadcast-btn" onClick={broadcast} disabled={broadcasting}
              className="btn-frost-orange inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm disabled:opacity-50">
              {broadcasting ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />} Diffuser maintenant
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PlaybookSection() {
  return (
    <div className="space-y-4" data-testid="playbook-section">
      <p className="text-foreground/60">Sept canaux concrets pour promouvoir Cocoon, avec pour chacun une action et un modèle de message.</p>
      {PLAYBOOK.map((p, i) => (
        <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
          className="glass rounded-2xl p-5 frame-pulse">
          <div className="flex items-center gap-3 mb-2">
            <span className="font-display text-primary/60 text-sm tabular-nums">{String(i + 1).padStart(2, "0")}</span>
            <h4 className="font-display text-lg text-gradient-orange">{p.channel}</h4>
          </div>
          <p className="text-foreground/70 text-sm leading-relaxed mb-3">{p.tip}</p>
          <div className="bg-black/25 border border-white/10 rounded-xl px-4 py-3 text-sm text-foreground/60 italic">« {p.template} »</div>
        </motion.div>
      ))}
    </div>
  );
}

function KitSection() {
  const [links, setLinks] = useState(null);
  const [banner, setBanner] = useState(null);
  const [gen, setGen] = useState(false);
  const origin = window.location.origin;
  useEffect(() => { api.get("/admin/promo/links").then(({ data }) => setLinks(data)).catch(() => setLinks({ podcasts: [] })); }, []);
  const copy = async (url) => { try { await navigator.clipboard.writeText(url); } catch {} toast.success("Lien copié"); };
  const makeBanner = async () => {
    setGen(true);
    try {
      const { data } = await api.post("/admin/promo/banner", { lang: "fr" });
      setBanner(`${API}/api/promo/image/${data.image_id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur de génération");
    } finally { setGen(false); }
  };
  const shareLinks = [
    { label: "Page d'accueil", url: origin + "/" },
    { label: "L'Écho (sujets publics)", url: origin + "/echo" },
    { label: "Les Tarifs", url: origin + "/tarifs" },
    { label: "Le Manifeste", url: origin + "/manifeste" },
  ];
  return (
    <div className="space-y-6" data-testid="kit-section">
      <div className="glass rounded-2xl p-6 frame-pulse">
        <h4 className="font-display text-lg text-foreground/85 mb-4">Liens à partager</h4>
        <div className="space-y-2">
          {shareLinks.map((l) => (
            <div key={l.url} className="flex items-center gap-3 bg-black/25 rounded-full px-4 py-2.5">
              <span className="text-sm text-foreground/70 flex-shrink-0 w-44">{l.label}</span>
              <span className="text-xs text-foreground/40 truncate flex-1">{l.url}</span>
              <button onClick={() => copy(l.url)} className="text-primary hover:text-primary/80 flex-shrink-0"><Copy size={15} /></button>
              <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-foreground/40 hover:text-primary flex-shrink-0"><ExternalLink size={15} /></a>
            </div>
          ))}
          {(links?.podcasts || []).map((p) => (
            <div key={p.id} className="flex items-center gap-3 bg-black/25 rounded-full px-4 py-2.5">
              <span className="text-sm text-foreground/70 flex-shrink-0 w-44 truncate">🎧 {p.title}</span>
              <span className="text-xs text-foreground/40 truncate flex-1">{origin}/podcast/{p.id}</span>
              <button onClick={() => copy(`${origin}/podcast/${p.id}`)} className="text-primary hover:text-primary/80 flex-shrink-0"><Copy size={15} /></button>
            </div>
          ))}
        </div>
      </div>
      <div className="glass rounded-2xl p-6 frame-pulse">
        <h4 className="font-display text-lg text-foreground/85 mb-2">Bannière promotionnelle (IA)</h4>
        <p className="text-foreground/55 text-sm mb-4">Génère une bannière cinématique aux couleurs de Cocoon, à réutiliser sur tes réseaux.</p>
        <button data-testid="banner-generate-btn" onClick={makeBanner} disabled={gen}
          className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm disabled:opacity-50">
          {gen ? <Loader2 size={17} className="animate-spin" /> : <ImageIcon size={17} />} Générer une bannière
        </button>
        {banner && (
          <div className="mt-5">
            <img src={banner} alt="Bannière Cocoon" className="w-full rounded-2xl border border-primary/20" data-testid="banner-img" />
            <a href={banner} download target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 mt-3">
              <Download size={15} /> Télécharger
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function ReferralSection() {
  const [me, setMe] = useState(null);
  const [board, setBoard] = useState(null);
  const origin = window.location.origin;
  useEffect(() => {
    api.get("/referral/me").then(({ data }) => setMe(data)).catch(() => {});
    api.get("/admin/referrals").then(({ data }) => setBoard(data)).catch(() => {});
  }, []);
  const link = me ? `${origin}/?ref=${me.code}` : "";
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); toast.success("Lien de parrainage copié"); }
    catch { toast.error("Copie impossible, sélectionne le lien manuellement."); }
  };
  return (
    <div className="space-y-6" data-testid="referral-section">
      <div className="glass rounded-2xl p-6 frame-pulse">
        <h4 className="font-display text-lg text-foreground/85 mb-2">Ton lien de parrainage</h4>
        <p className="text-foreground/55 text-sm mb-4">Partage-le : chaque inscription via ce lien est comptabilisée. Un levier d'acquisition gratuit et mesurable.</p>
        {me ? (
          <>
            <div className="flex items-center gap-2 bg-black/30 border border-white/10 rounded-full px-4 py-2.5">
              <input readOnly value={link} data-testid="referral-link" className="flex-1 bg-transparent text-sm text-foreground/70 outline-none truncate" />
              <button onClick={copy} className="text-primary hover:text-primary/80 flex-shrink-0"><Copy size={16} /></button>
            </div>
            <p className="text-sm text-foreground/50 mt-3"><span className="text-primary font-semibold">{me.count}</span> explorateur(s) parrainé(s) par toi.</p>
          </>
        ) : (
          <div className="flex items-center gap-2 text-foreground/40 text-sm"><Loader2 size={15} className="animate-spin" /> Chargement…</div>
        )}
      </div>
      <div className="glass rounded-2xl p-6 frame-pulse">
        <h4 className="font-display text-lg text-foreground/85 mb-4">Meilleurs parrains ({board?.total_referred ?? 0} parrainages au total)</h4>
        {board && board.top.length > 0 ? (
          <div className="space-y-2">
            {board.top.map((r, i) => (
              <div key={i} className="flex items-center justify-between bg-black/25 rounded-full px-4 py-2.5">
                <span className="text-sm text-foreground/75 flex items-center gap-3"><span className="text-primary/60 tabular-nums">{i + 1}</span> {r.name}</span>
                <span className="text-sm text-primary tabular-nums">{r.count}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-foreground/40 italic">Aucun parrainage pour l'instant. Partage ton lien pour lancer la machine.</p>
        )}
      </div>
    </div>
  );
}

function MessagesSection() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/messages").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = async (id) => {
    try { await api.post(`/admin/messages/${id}/handled`); load(); } catch { toast.error("Action impossible"); }
  };
  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch { return ""; }
  };
  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary/60" size={28} /></div>;
  return (
    <div className="space-y-4" data-testid="messages-section">
      <p className="text-foreground/60">{data.messages.length} message(s) reçu(s) via le formulaire de contact — <span className="text-primary">{data.unread} non traité(s)</span>.</p>
      {data.messages.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center text-foreground/40 italic">Aucun message pour l'instant.</div>
      ) : data.messages.map((m) => (
        <motion.div key={m.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className={`glass rounded-2xl p-5 ${m.handled ? "opacity-60" : "ring-1 ring-primary/25"}`} data-testid={`message-${m.id}`}>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-4 mb-2">
            <div className="min-w-0">
              <span className="font-display text-lg text-foreground/90">{m.name || "Explorateur"}</span>
              <span className="text-sm text-foreground/45 ml-2 break-all">{m.email}</span>
            </div>
            <span className="text-xs text-foreground/40 whitespace-nowrap flex-shrink-0 tabular-nums">{fmt(m.created_at)}</span>
          </div>
          <p className="text-foreground/70 text-sm leading-relaxed whitespace-pre-wrap mb-4">{m.message}</p>
          <div className="flex items-center gap-3">
            <a href={`mailto:${m.email}?subject=Réponse — COCOON`} className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80" data-testid={`message-reply-${m.id}`}>
              <Reply size={15} /> Répondre
            </a>
            <button onClick={() => toggle(m.id)} data-testid={`message-toggle-${m.id}`}
              className={`inline-flex items-center gap-1.5 text-sm ml-auto ${m.handled ? "text-foreground/40 hover:text-foreground/70" : "text-emerald-300 hover:text-emerald-200"}`}>
              <CheckCircle2 size={15} /> {m.handled ? "Traité" : "Marquer traité"}
            </button>
          </div>
        </motion.div>
      ))}
    </div>
  );
}


function GuestRequestsSection() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/guest-requests").then(({ data }) => setData(data)).catch(() => setData({ requests: [], total: 0, pending: 0 }));
  useEffect(() => { load(); }, []);
  const toggle = async (id, handled) => {
    try { await api.patch(`/admin/guest-requests/${id}?handled=${handled}`); load(); } catch { toast.error("Action impossible"); }
  };
  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
  };
  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary/60" size={28} /></div>;
  return (
    <div className="space-y-4" data-testid="guests-section">
      <p className="text-foreground/60">{data.total} sujet(s) / thème(s) proposé(s) pour La Dérive — <span className="text-primary">{data.pending} à traiter</span>.</p>
      {data.requests.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center text-foreground/40 italic">Aucun sujet proposé pour l'instant.</div>
      ) : data.requests.map((g) => (
        <motion.div key={g.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className={`glass rounded-2xl p-5 ${g.handled ? "opacity-60" : "ring-1 ring-primary/25"}`} data-testid={`guest-${g.id}`}>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-4 mb-2">
            <div className="min-w-0">
              <span className="font-display text-lg text-foreground/90">{g.name || "Explorateur"}</span>
              <span className="text-sm text-foreground/45 ml-2 break-all">{g.email}</span>
              <span className="text-[10px] uppercase tracking-widest text-primary/60 ml-2">{g.lang}</span>
            </div>
            <span className="text-xs text-foreground/40 whitespace-nowrap flex-shrink-0 tabular-nums">{fmt(g.created_at)}</span>
          </div>
          <p className="text-foreground/70 text-sm leading-relaxed whitespace-pre-wrap mb-4">{g.topic}</p>
          <div className="flex items-center gap-3">
            <a href={`mailto:${g.email}?subject=${encodeURIComponent("Ton sujet proposé — podcast La Dérive")}`} className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80" data-testid={`guest-reply-${g.id}`}>
              <Reply size={15} /> Répondre
            </a>
            <button onClick={() => toggle(g.id, !g.handled)} data-testid={`guest-toggle-${g.id}`}
              className={`inline-flex items-center gap-1.5 text-sm ml-auto ${g.handled ? "text-foreground/40 hover:text-foreground/70" : "text-emerald-300 hover:text-emerald-200"}`}>
              <CheckCircle2 size={15} /> {g.handled ? "Traité" : "Marquer traité"}
            </button>
          </div>
        </motion.div>
      ))}
    </div>
  );
}


function GardenAdminSection() {
  const [ideas, setIdeas] = useState(null);
  const load = () => api.get("/admin/garden/ideas").then(({ data }) => setIdeas(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    try { await api.delete(`/garden/ideas/${id}`); load(); } catch { toast.error("Action impossible"); }
  };
  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
  };
  if (!ideas) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary/60" size={28} /></div>;
  return (
    <div className="space-y-4" data-testid="garden-admin-section">
      <p className="text-foreground/60">{ideas.length} idée(s) déposée(s) dans Le Jardin des idées.</p>
      {ideas.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center text-foreground/40 italic">Aucune idée pour l'instant.</div>
      ) : ideas.map((i) => (
        <motion.div key={i.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="glass rounded-2xl p-5" data-testid={`garden-admin-idea-${i.id}`}>
          <div className="flex items-start justify-between gap-4 mb-2">
            <div className="min-w-0">
              <span className="font-display text-lg text-foreground/90">{i.user_name}</span>
              <span className="text-xs text-primary ml-2">{i.votes} vote(s)</span>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="text-xs text-foreground/40 whitespace-nowrap tabular-nums">{fmt(i.created_at)}</span>
              <button onClick={() => del(i.id)} className="text-foreground/40 hover:text-red-300" data-testid={`garden-admin-delete-${i.id}`}><Trash2 size={16} /></button>
            </div>
          </div>
          <p className="text-foreground/70 text-sm leading-relaxed whitespace-pre-wrap">{i.content}</p>
        </motion.div>
      ))}
    </div>
  );
}

function mdToHtmlPreview(text) {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return (text || "").split("\n\n").map((block) => {
    const b = block.trim();
    if (!b) return "";
    let h = esc(b).replace(/\*\*(.+?)\*\*/g, "<strong class='text-foreground/95'>$1</strong>");
    if (h.startsWith("## ")) return `<h3 class="font-display italic text-primary text-lg mt-4 mb-1">${h.slice(3)}</h3>`;
    if (h.startsWith("# ")) return `<h3 class="font-display text-primary text-xl mt-5 mb-2">${h.slice(2)}</h3>`;
    return `<p class="mb-3">${h.replace(/\n/g, "<br>")}</p>`;
  }).join("");
}

const escEmail = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
function mdToEmailHtml(text) {
  return (text || "").split("\n\n").map((block) => {
    const b = block.trim();
    if (!b) return "";
    let h = escEmail(b).replace(/\*\*(.+?)\*\*/g, "<strong style='color:#efe6d0'>$1</strong>");
    if (h.startsWith("## ")) return `<h2 style="color:#e08a2b;font-family:Georgia,serif;font-style:italic;font-weight:normal;font-size:20px;margin:22px 0 6px">${h.slice(3)}</h2>`;
    if (h.startsWith("# ")) return `<h2 style="color:#e08a2b;font-family:Georgia,serif;font-size:23px;margin:24px 0 8px">${h.slice(2)}</h2>`;
    return `<p style="margin:0 0 14px">${h.replace(/\n/g, "<br>")}</p>`;
  }).join("");
}
function buildEmailHtml(subject, body) {
  return `<!doctype html><html><head><meta name="viewport" content="width=device-width,initial-scale=1"></head><body style="margin:0"><div style="background:#070a10;padding:32px 16px;font-family:Arial,sans-serif"><div style="max-width:500px;margin:0 auto;background:#0b0e14;border:2px solid #c69a3e;border-radius:20px;overflow:hidden;box-shadow:0 0 0 1px rgba(224,138,43,0.2),0 18px 48px rgba(0,0,0,0.5)"><div style="background:radial-gradient(130% 120% at 50% 15%, rgba(232,150,60,0.55), rgba(11,14,20,0) 68%), linear-gradient(180deg,#4a2e14,#0b0e14);padding:42px 0;text-align:center"><span style="font-family:Georgia,serif;font-style:italic;font-size:34px;background:linear-gradient(90deg,#FDE68A,#F59E0B,#B45309);-webkit-background-clip:text;background-clip:text;color:#F59E0B;text-shadow:0 0 26px rgba(245,158,11,0.5)">COCOON MIND</span></div><div style="padding:6px 26px 30px"><div style="color:#e08a2b;font-size:12px;letter-spacing:3px;text-transform:uppercase;margin-top:18px">Le Journal de Cocoon</div><h1 style="color:#efe6d0;font-family:Georgia,serif;font-style:italic;font-weight:normal;font-size:26px;margin:12px 0">${escEmail(subject) || "Sans titre"}</h1><div style="color:#cdbfa5;line-height:1.7;font-size:15px;font-family:Georgia,serif">${mdToEmailHtml(body)}</div><a href="#" style="display:inline-block;margin-top:22px;background:#e08a2b;color:#1a1206;text-decoration:none;padding:13px 28px;border-radius:999px;font-weight:bold;font-family:Arial">Ouvrir Cocoon</a><p style="color:#6b6455;font-size:12px;margin-top:26px">cocoonmind.com</p></div></div></div></body></html>`;
}

const DEFAULT_NEWSLETTER = `Contrairement à l'idée reçue selon laquelle l'IA serait un outil de « paresse », elle est en réalité un amplificateur de capacités qui exige souvent plus de rigueur de la part de l'utilisateur.

Voici pourquoi l'IA demande, au contraire, une forme d'exigence intellectuelle :

## 1. La qualité de l'entrée définit la sortie

Pour obtenir un résultat pertinent, il ne suffit pas de cliquer sur un bouton. Il faut savoir formuler sa pensée, structurer sa requête et définir des contraintes précises. Une personne « fainéante » qui pose des questions vagues obtiendra des résultats médiocres, inutilisables ou génériques. **L'IA récompense ceux qui savent poser les bonnes questions.**

## 2. Le rôle critique de l'esprit critique

L'IA peut halluciner ou présenter des informations avec une assurance trompeuse. L'utiliser efficacement impose la **vérification des faits** (croiser les informations), le **discernement** (juger la pertinence, le ton et la logique) et l'**édition** : l'IA produit une base, mais c'est le travail humain qui apporte la valeur ajoutée, la nuance et la signature personnelle.

## 3. Le passage de l'exécution à la supervision

Avec l'IA, on passe du rôle d'exécutant à celui de chef d'orchestre. Cela demande une vision d'ensemble et une capacité à diriger un processus plutôt que d'effectuer une tâche répétitive. Cela demande de l'énergie mentale et de l'organisation.

## 4. La remise en question constante

L'IA évolue vite. Pour rester efficace, il faut continuellement apprendre de nouvelles méthodes, comprendre les limites des modèles et adapter ses processus. C'est une démarche d'apprentissage actif, aux antipodes de l'immobilisme.

En somme, l'IA permet d'économiser du temps sur les tâches laborieuses et répétitives, mais elle demande en échange une meilleure maîtrise de son sujet. **Elle ne remplace pas l'effort : elle le déplace du « faire » vers le « penser » et le « diriger ».**`;

function NewsletterSection() {
  const [subject, setSubject] = useState("L'IA n'est pas pour les fainéants");
  const [body, setBody] = useState(DEFAULT_NEWSLETTER);
  const [audience, setAudience] = useState(null);
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [translate, setTranslate] = useState(false);
  const [history, setHistory] = useState([]);
  const [segment, setSegment] = useState("all");
  const [scheduledAt, setScheduledAt] = useState("");
  const [scheduling, setScheduling] = useState(false);
  const [scheduled, setScheduled] = useState([]);
  const [previewLang, setPreviewLang] = useState("fr");
  const [previews, setPreviews] = useState({});
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewEdit, setPreviewEdit] = useState(false);
  const [weekly, setWeekly] = useState(null);
  const [weeklyBusy, setWeeklyBusy] = useState(false);

  const loadHistory = () => api.get("/admin/newsletter/history").then(({ data }) => setHistory(data.items || [])).catch(() => {});
  const loadScheduled = () => api.get("/admin/newsletter/scheduled").then(({ data }) => setScheduled(data.items || [])).catch(() => {});
  const loadWeekly = () => api.get("/admin/newsletter/weekly").then(({ data }) => setWeekly(data)).catch(() => {});
  useEffect(() => {
    api.get("/admin/derive/audience").then(({ data }) => setAudience(data)).catch(() => {});
    loadHistory();
    loadScheduled();
    loadWeekly();
  }, []);

  const cocon = audience?.cocon || 0;
  const landing = audience?.landing || 0;
  const total = cocon + landing;
  const recipientCount = segment === "cocon" ? cocon : segment === "free" ? landing : total;
  const SEGMENTS = [
    { id: "all", label: "Tous", n: total },
    { id: "cocon", label: "Abonnés Cocoon", n: cocon },
    { id: "free", label: "Inscrits gratuits", n: landing },
  ];

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/newsletter/send", { subject, body, test_only: true, translate, source_lang: "fr", variants: editedVariants() });
      const langs = (data.langs || []).map((l) => l.toUpperCase()).join(", ");
      toast.success(data.sent ? (translate ? `${data.sent} e-mail(s) de test envoyé(s) à ${data.to} — langues : ${langs}` : `E-mail de test envoyé à ${data.to}`) : "Envoi de test échoué (voir clé Resend / domaine)");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Envoi de test impossible");
    } finally { setTesting(false); }
  };

  const sendAll = async () => {
    if (!window.confirm(`Envoyer cette newsletter à ${recipientCount} destinataire(s) [${segment}] par e-mail ? Action immédiate et irréversible.`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/newsletter/send", { subject, body, test_only: false, translate, source_lang: "fr", segment, variants: editedVariants() });
      toast.success(`Newsletter envoyée — ${data.sent} e-mail(s) sur ${data.total}${data.failed ? ` · ${data.failed} échec(s)` : ""}`);
      loadHistory();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Envoi impossible");
    } finally { setSending(false); }
  };

  const scheduleSend = async () => {
    if (!scheduledAt) { toast.error("Choisis une date et une heure."); return; }
    setScheduling(true);
    try {
      const iso = new Date(scheduledAt).toISOString();
      await api.post("/admin/newsletter/schedule", { subject, body, translate, source_lang: "fr", segment, scheduled_at: iso, variants: editedVariants() });
      toast.success("Newsletter programmée !");
      setScheduledAt("");
      loadScheduled();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Programmation impossible");
    } finally { setScheduling(false); }
  };

  const cancelScheduled = async (id) => {
    try {
      await api.delete(`/admin/newsletter/scheduled/${id}`);
      toast.success("Envoi programmé annulé");
      loadScheduled();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Annulation impossible");
    }
  };

  const loadTemplate = (tpl) => { setSubject(tpl.subject); setBody(tpl.body); toast.success(`Modèle chargé : ${tpl.label}`); };

  const toggleWeekly = async (enabled) => {
    setWeeklyBusy(true);
    try {
      await api.post("/admin/newsletter/weekly", { enabled });
      toast.success(enabled ? "Newsletter hebdo activée — envoi auto chaque dimanche 10h (Bruxelles)" : "Newsletter hebdo désactivée");
      loadWeekly();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action impossible"); }
    finally { setWeeklyBusy(false); }
  };
  const setWeeklyNext = async (idx) => {
    setWeeklyBusy(true);
    try { await api.post("/admin/newsletter/weekly", { set_index: idx }); toast.success("Prochain sujet mis à jour"); loadWeekly(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action impossible"); }
    finally { setWeeklyBusy(false); }
  };
  const toggleWeeklyAi = async (ai_generate) => {
    setWeeklyBusy(true);
    try {
      await api.post("/admin/newsletter/weekly", { ai_generate });
      toast.success(ai_generate ? "Sujets générés par l'IA chaque semaine" : "Retour aux sujets de la liste");
      loadWeekly();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action impossible"); }
    finally { setWeeklyBusy(false); }
  };
  const previewNextWeekly = async () => {
    if (weekly?.ai_generate) {
      setWeeklyBusy(true);
      try {
        const { data } = await api.post("/admin/newsletter/weekly/generate");
        setSubject(data.subject || "");
        setBody(data.body || "");
        toast.success("Sujet généré par l'IA chargé dans l'aperçu ci-dessous");
        setTimeout(() => document.querySelector("[data-testid='newsletter-preview']")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Génération impossible"); }
      finally { setWeeklyBusy(false); }
      return;
    }
    if (!weekly?.next_subject) return;
    setSubject(weekly.next_subject);
    setBody(weekly.next_body || "");
    toast.success("Prochain envoi chargé dans l'aperçu e-mail ci-dessous");
    setTimeout(() => document.querySelector("[data-testid='newsletter-preview']")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  useEffect(() => { setPreviews({}); setPreviewLang("fr"); }, [subject, body]);

  const selectPreviewLang = async (lg) => {
    if (lg === "fr" || previews[lg]) { setPreviewLang(lg); return; }
    setPreviewLoading(true);
    try {
      const { data } = await api.post("/admin/newsletter/preview", { subject, body, source_lang: "fr" });
      setPreviews(data.variants || {});
      setPreviewLang(lg);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Traduction de l'aperçu impossible");
    } finally { setPreviewLoading(false); }
  };

  const curSubject = previewLang === "fr" ? subject : (previews[previewLang]?.subject || subject);
  const curBody = previewLang === "fr" ? body : (previews[previewLang]?.body || body);
  const PREVIEW_LANGS = ["fr", "en", "es", "nl"];
  const updatePreviewField = (field, val) => setPreviews((p) => ({ ...p, [previewLang]: { ...(p[previewLang] || {}), [field]: val } }));
  const editedVariants = () => (translate && Object.keys(previews).length ? previews : undefined);

  return (
    <div className="space-y-6" data-testid="newsletter-section">
      <div className="glass rounded-2xl p-6 frame-pulse space-y-5">
        <p className="text-foreground/60">Compose une newsletter et envoie-la par e-mail à tous tes abonnés. Utilise <span className="text-primary">**gras**</span> et <span className="text-primary">## Sous-titre</span> pour la mise en forme. Fais toujours un envoi de test avant la diffusion.</p>

        {audience && (
          <div className="flex flex-wrap gap-3" data-testid="newsletter-audience">
            <div className="rounded-xl border border-primary/15 bg-primary/[0.04] px-4 py-2.5">
              <div className="text-lg font-display text-gradient-orange">{audience.cocon}</div>
              <div className="text-[0.7rem] uppercase tracking-wider text-foreground/45">abonnés Cocoon</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-2.5">
              <div className="text-lg font-display text-foreground/90">{audience.landing}</div>
              <div className="text-[0.7rem] uppercase tracking-wider text-foreground/45">inscrits confirmés</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-2.5">
              <div className="text-lg font-display text-foreground/90">{total}</div>
              <div className="text-[0.7rem] uppercase tracking-wider text-foreground/45">destinataires au total</div>
            </div>
          </div>
        )}

        <div data-testid="newsletter-templates">
          <label className="text-xs uppercase tracking-wider text-foreground/45 mb-1.5 block">Modèles prêts à charger</label>
          <div className="flex flex-wrap gap-2">
            {NEWSLETTER_TEMPLATES.map((tpl) => (
              <button key={tpl.id} onClick={() => loadTemplate(tpl)} data-testid={`newsletter-tpl-${tpl.id}`}
                className="text-xs px-3 py-1.5 rounded-full border border-primary/20 text-foreground/70 hover:text-primary hover:bg-primary/[0.06] hover:border-primary/40 transition-colors">
                {tpl.label}
              </button>
            ))}
          </div>
        </div>

        <div data-testid="newsletter-segment">
          <label className="text-xs uppercase tracking-wider text-foreground/45 mb-1.5 block">Destinataires</label>
          <div className="flex flex-wrap gap-2">
            {SEGMENTS.map((s) => (
              <button key={s.id} onClick={() => setSegment(s.id)} data-testid={`newsletter-seg-${s.id}`}
                className={`text-sm px-4 py-2 rounded-full border transition-colors ${segment === s.id ? "border-primary/50 bg-primary/15 text-primary" : "border-white/10 text-foreground/60 hover:border-primary/30"}`}>
                {s.label} <span className="opacity-60">({s.n})</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-foreground/45 mb-1.5 block">Objet de l'e-mail</label>
          <input data-testid="newsletter-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
            className="w-full bg-black/30 border border-white/15 rounded-xl px-4 py-3 text-foreground/90 outline-none focus:border-primary/40" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-foreground/45 mb-1.5 block">Contenu</label>
          <textarea data-testid="newsletter-body" value={body} onChange={(e) => setBody(e.target.value)} rows={14}
            className="w-full bg-black/30 border border-white/10 rounded-2xl p-4 text-foreground/85 leading-relaxed resize-y outline-none focus:border-primary/40 font-mono text-sm" />
        </div>

        <label className="flex items-center gap-2.5 text-sm text-foreground/70 cursor-pointer select-none" data-testid="newsletter-translate">
          <input type="checkbox" checked={translate} onChange={(e) => setTranslate(e.target.checked)} className="accent-[#e08a2b] w-4 h-4" />
          Traduire automatiquement pour chaque abonné (EN · ES · NL) — le français reste l'original
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <button data-testid="newsletter-test-btn" onClick={sendTest} disabled={testing || !subject.trim() || body.trim().length < 10}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors font-semibold text-sm disabled:opacity-50">
            {testing ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} Envoi de test (à moi)
          </button>
          <button data-testid="newsletter-send-btn" onClick={sendAll} disabled={sending || !subject.trim() || body.trim().length < 10}
            className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm disabled:opacity-50">
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />} Envoyer à tous ({recipientCount})
          </button>
        </div>

        <div className="border-t border-white/10 pt-4" data-testid="newsletter-schedule">
          <label className="text-xs uppercase tracking-wider text-foreground/45 mb-1.5 block">Programmer un envoi</label>
          <div className="flex flex-wrap items-center gap-3">
            <input type="datetime-local" data-testid="newsletter-schedule-input" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)}
              className="bg-black/30 border border-white/15 rounded-xl px-4 py-2.5 text-foreground/90 outline-none focus:border-primary/40 text-sm" />
            <button data-testid="newsletter-schedule-btn" onClick={scheduleSend} disabled={scheduling || !scheduledAt || !subject.trim() || body.trim().length < 10}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors font-semibold text-sm disabled:opacity-50">
              {scheduling ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} />} Programmer
            </button>
          </div>
          {scheduled.filter((s) => s.status === "pending").length > 0 && (
            <div className="mt-3 space-y-2" data-testid="newsletter-scheduled-list">
              {scheduled.filter((s) => s.status === "pending").map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-2 rounded-xl border border-primary/15 bg-primary/[0.03] px-4 py-2.5" data-testid="newsletter-scheduled-item">
                  <div className="min-w-0">
                    <div className="text-sm text-foreground/85 truncate">{s.subject}</div>
                    <div className="text-[0.7rem] text-foreground/40">{s.scheduled_at ? new Date(s.scheduled_at).toLocaleString() : ""} · {s.segment}{s.translated ? " · traduit" : ""}</div>
                  </div>
                  <button onClick={() => cancelScheduled(s.id)} className="text-xs text-red-300/80 hover:text-red-300 border border-red-400/25 rounded-full px-3 py-1.5 flex-shrink-0">Annuler</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="glass rounded-2xl p-6" data-testid="newsletter-preview">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="text-[0.7rem] uppercase tracking-wider text-primary/60">Aperçu e-mail</div>
          <div className="flex items-center gap-1.5" data-testid="newsletter-preview-langs">
            {PREVIEW_LANGS.map((lg) => (
              <button key={lg} onClick={() => selectPreviewLang(lg)} disabled={previewLoading} data-testid={`newsletter-preview-lang-${lg}`}
                className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors disabled:opacity-40 ${previewLang === lg ? "border-primary/50 bg-primary/15 text-primary" : "border-white/10 text-foreground/55 hover:border-primary/30 hover:text-foreground/80"}`}>
                {lg}{lg !== "fr" && !previews[lg] ? " ✦" : ""}
              </button>
            ))}
            {previewLoading && <span className="text-xs text-foreground/40 ml-1" data-testid="newsletter-preview-loading">traduction…</span>}
            {previewLang !== "fr" && previews[previewLang] && (
              <button onClick={() => setPreviewEdit((v) => !v)} data-testid="newsletter-preview-edit-toggle"
                className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ml-1 ${previewEdit ? "border-primary/50 bg-primary/15 text-primary" : "border-white/10 text-foreground/55 hover:border-primary/30"}`}>
                {previewEdit ? "Aperçu" : "✎ Éditer"}
              </button>
            )}
          </div>
        </div>
        {previewLang !== "fr" && (
          <div className="text-[0.68rem] text-foreground/40 mb-3" data-testid="newsletter-preview-note">
            Traduction automatique ({previewLang.toUpperCase()}) — {previewEdit ? "modifie le texte ci-dessous, il sera utilisé tel quel à l'envoi." : "aperçu uniquement, aucun e-mail envoyé."}
          </div>
        )}
        {previewLang === "fr" && (
          <div className="text-[0.68rem] text-foreground/40 mb-3">Version source — édite-la dans le champ de composition ci-dessus.</div>
        )}

        {previewEdit && previewLang !== "fr" ? (
          <div className="space-y-3" data-testid="newsletter-preview-editor">
            <input value={curSubject} onChange={(e) => updatePreviewField("subject", e.target.value)} data-testid="newsletter-edit-subject"
              className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2.5 text-foreground/90 focus:border-primary/40 outline-none" placeholder="Titre traduit" />
            <textarea value={curBody} onChange={(e) => updatePreviewField("body", e.target.value)} data-testid="newsletter-edit-body" rows={14}
              className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-foreground/80 text-sm leading-relaxed focus:border-primary/40 outline-none font-mono" placeholder="Corps traduit (markdown)" />
          </div>
        ) : (
          <iframe title="newsletter-email-preview" data-testid="newsletter-preview-frame"
            srcDoc={buildEmailHtml(curSubject, curBody)}
            className="w-full rounded-xl border border-white/10" style={{ height: 560, background: "#0b0e14" }} />
        )}
      </div>

      {weekly && (
        <div className="glass rounded-2xl p-6 frame-pulse" data-testid="weekly-newsletter-panel">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 text-primary/80 text-[0.7rem] uppercase tracking-wider mb-1.5">
                <Radio size={14} /> Newsletter hebdomadaire automatique
              </div>
              <h3 className="font-display text-2xl text-gradient-orange leading-tight">Un sujet, chaque dimanche 10 h</h3>
              <p className="text-sm text-foreground/55 mt-1.5 max-w-lg leading-relaxed">
                Envoi automatique à <span className="text-foreground/80">tous les abonnés</span>, avec traduction EN/ES/NL. {weekly.ai_generate ? "Sujets générés par l'IA — file infinie." : `${weekly.total_topics} sujets en file, dans l'ordre.`}
              </p>
            </div>
            <button
              onClick={() => toggleWeekly(!weekly.enabled)}
              disabled={weeklyBusy}
              data-testid="weekly-toggle-btn"
              aria-pressed={weekly.enabled}
              className={`relative inline-flex h-9 w-16 items-center rounded-full border transition-colors duration-300 disabled:opacity-50 ${weekly.enabled ? "bg-primary/25 border-primary/50" : "bg-white/[0.04] border-white/15"}`}
            >
              <span className={`inline-block h-7 w-7 transform rounded-full transition-transform duration-300 ${weekly.enabled ? "translate-x-8 bg-primary" : "translate-x-1 bg-foreground/40"}`} />
            </button>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-primary/15 bg-primary/[0.03] px-4 py-3" data-testid="weekly-ai-row">
            <div className="flex items-center gap-2.5">
              <Sparkles size={16} className="text-primary" />
              <div>
                <div className="text-sm text-foreground/85 font-medium">Sujets générés par l'IA (automatique)</div>
                <div className="text-[0.7rem] text-foreground/45">Un nouveau sujet original chaque semaine, sans jamais se répéter.</div>
              </div>
            </div>
            <button
              onClick={() => toggleWeeklyAi(!weekly.ai_generate)}
              disabled={weeklyBusy}
              data-testid="weekly-ai-toggle"
              aria-pressed={weekly.ai_generate}
              className={`relative inline-flex h-8 w-14 items-center rounded-full border transition-colors duration-300 disabled:opacity-50 flex-shrink-0 ${weekly.ai_generate ? "bg-primary/25 border-primary/50" : "bg-white/[0.04] border-white/15"}`}
            >
              <span className={`inline-block h-6 w-6 transform rounded-full transition-transform duration-300 ${weekly.ai_generate ? "translate-x-7 bg-primary" : "translate-x-1 bg-foreground/40"}`} />
            </button>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className={`text-xs px-3 py-1.5 rounded-full border ${weekly.enabled ? "border-emerald-300/40 text-emerald-300 bg-emerald-300/10" : "border-white/15 text-foreground/50"}`} data-testid="weekly-status">
              {weekly.enabled ? "Activée" : "Désactivée"}
            </span>
            <button
              onClick={previewNextWeekly}
              disabled={weeklyBusy}
              data-testid="weekly-preview-next-btn"
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/40 text-primary hover:bg-primary/10 transition-colors text-xs font-semibold disabled:opacity-50"
            >
              {weeklyBusy && weekly.ai_generate ? <Loader2 size={14} className="animate-spin" /> : weekly.ai_generate ? <Sparkles size={14} /> : <Eye size={14} />}
              {weekly.ai_generate ? "Générer un aperçu (IA)" : "Prévisualiser le prochain envoi"}
            </button>
            {weekly.last_sent_at && (
              <span className="text-[0.7rem] text-foreground/40">Dernier envoi : {new Date(weekly.last_sent_at).toLocaleString()} — « {weekly.last_subject} »</span>
            )}
          </div>

          {weekly.ai_generate ? (
            <div className="mt-5 rounded-xl border border-white/8 bg-white/[0.02] px-4 py-4 text-sm text-foreground/55 leading-relaxed" data-testid="weekly-ai-note">
              <Sparkles size={14} className="inline text-primary mr-1.5" />
              Le sujet est <span className="text-foreground/80">créé automatiquement par l'IA</span> au moment de l'envoi, dans le ton de Cocoon, en évitant les sujets déjà traités. Utilise « Générer un aperçu » pour voir un exemple.
              {subject && (
                <div className="mt-3">
                  <button
                    onClick={previewNextWeekly}
                    disabled={weeklyBusy}
                    data-testid="weekly-regenerate-btn"
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/40 text-primary hover:bg-primary/10 transition-colors text-xs font-semibold disabled:opacity-50"
                  >
                    {weeklyBusy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Régénérer un autre sujet
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-5">
              <div className="text-[0.7rem] uppercase tracking-wider text-foreground/45 mb-2">File des sujets (clique pour définir le prochain)</div>
              <div className="space-y-1.5 max-h-72 overflow-y-auto scroll-thin pr-1" data-testid="weekly-queue">
                {(weekly.queue || []).map((q) => (
                  <button
                    key={q.id}
                    onClick={() => setWeeklyNext(q.position)}
                    disabled={weeklyBusy}
                    data-testid={`weekly-queue-item-${q.id}`}
                    className={`w-full text-left flex items-center gap-3 rounded-xl border px-4 py-2.5 transition-colors disabled:opacity-60 ${q.is_next ? "border-primary/50 bg-primary/10" : "border-white/8 bg-white/[0.02] hover:border-primary/25"}`}
                  >
                    <span className={`text-xs font-mono w-6 flex-shrink-0 ${q.is_next ? "text-primary" : "text-foreground/35"}`}>{String(q.position + 1).padStart(2, "0")}</span>
                    <span className={`text-sm truncate ${q.is_next ? "text-foreground/90" : "text-foreground/60"}`}>{q.subject}</span>
                    {q.is_next && <span className="ml-auto text-[0.6rem] uppercase tracking-wider text-primary flex-shrink-0 flex items-center gap-1"><Send size={11} /> prochain</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {history.length > 0 && (
        <div className="glass rounded-2xl p-6" data-testid="newsletter-history">
          <div className="text-[0.7rem] uppercase tracking-wider text-primary/60 mb-3">Historique des envois</div>
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3" data-testid="newsletter-history-item">
                <div className="min-w-0">
                  <div className="text-sm text-foreground/85 truncate">{h.subject}</div>
                  <div className="text-[0.7rem] text-foreground/40">{h.created_at ? new Date(h.created_at).toLocaleString() : ""}{h.translated ? ` · traduit (${(h.langs || []).filter((l) => l !== "fr").join(", ") || "—"})` : ""}</div>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-emerald-300/90">{h.sent} envoyé{h.sent > 1 ? "s" : ""}</span>
                  {h.failed > 0 && <span className="text-red-300/90">{h.failed} échec{h.failed > 1 ? "s" : ""}</span>}
                  <span className="text-foreground/45">/ {h.total}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("kpis");
  return (
    <div className="min-h-screen bg-background text-foreground starfield">
      <header className="glass sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button data-testid="admin-back-btn" onClick={() => navigate("/atelier")} className="text-foreground/50 hover:text-primary transition-colors"><ArrowLeft size={20} /></button>
            <span className="font-display text-xl text-gradient-orange">Dashboard Publicité</span>
          </div>
          <span className="text-sm text-foreground/40 hidden sm:block">{user?.name} · admin</span>
        </div>
        <nav className="max-w-6xl mx-auto px-4 sm:px-6 pb-3 flex items-center gap-1 overflow-x-auto scroll-thin">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const active = section === s.id;
            return (
              <button key={s.id} data-testid={`admin-tab-${s.id}`} onClick={() => setSection(s.id)}
                className={`whitespace-nowrap flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${active ? "bg-primary/15 text-primary" : "text-foreground/50 hover:text-foreground/80"}`}>
                <Icon size={15} /> {s.label}
              </button>
            );
          })}
        </nav>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-10">
        <motion.div key={section} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          {section === "kpis" && <KpisSection />}
          {section === "monitoring" && <MonitoringSection />}
          {section === "promo" && <PromoSection />}
          {section === "tiktok" && <TikTokSection />}
          {section === "derive" && <DeriveSection />}
          {section === "newsletter" && <NewsletterSection />}
          {section === "playbook" && <PlaybookSection />}
          {section === "kit" && <KitSection />}
          {section === "referral" && <ReferralSection />}
          {section === "conferences" && <ConferencesSection />}
          {section === "partners" && <PartnersLetterSection />}
          {section === "analytics" && <AnalyticsSection />}
          {section === "users" && <UsersSection />}
          {section === "garden" && <GardenAdminSection />}
          {section === "messages" && <MessagesSection />}
          {section === "guests" && <GuestRequestsSection />}
          {section === "radio" && <RadioChannelsAdmin />}
          {section === "maintenance" && <MaintenanceSection />}
        </motion.div>
      </main>
    </div>
  );
}
